import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Services', href: '/services' },
    { name: 'Work', href: '/portfolio' },
    { name: 'About', href: '/about' },
    { name: 'Demo', href: '/demo' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass py-3' : 'bg-transparent py-5'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_focusagency/artifacts/gtut8c09_FP%20Logo%20Straight%20White.png"
              alt="Focus Pixel"
              className="h-7"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? 'text-primary'
                    : 'text-white/70 hover:text-white'
                }`}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Link
                  to={user?.role === 'client' ? '/client/dashboard' : '/admin/leads'}
                  className="text-sm text-white/70 hover:text-white transition-colors font-medium"
                  data-testid="nav-dashboard"
                >
                  Dashboard
                </Link>
                <button
                  onClick={logout}
                  className="text-sm text-white/70 hover:text-white transition-colors"
                  data-testid="nav-logout"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="text-sm text-white/70 hover:text-white transition-colors font-medium"
                data-testid="nav-login"
              >
                Client Login
              </Link>
            )}
            <Link
              to="/book-call"
              className="btn-primary text-sm py-2.5 px-5 flex items-center gap-2"
              data-testid="nav-book-call"
            >
              Book a Strategy Call
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-white"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="md:hidden glass mt-2 mx-4 rounded-xl overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`block text-lg font-medium ${
                    isActive(link.href) ? 'text-primary' : 'text-white/70'
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-white/10 space-y-4">
                {isAuthenticated ? (
                  <>
                    <Link
                      to={user?.role === 'client' ? '/client/dashboard' : '/admin/leads'}
                      onClick={() => setIsOpen(false)}
                      className="block text-lg text-white/70 font-medium"
                      data-testid="mobile-nav-dashboard"
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setIsOpen(false);
                      }}
                      className="block text-lg text-white/70"
                      data-testid="mobile-nav-logout"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setIsOpen(false)}
                    className="block text-lg text-white/70"
                    data-testid="mobile-nav-login"
                  >
                    Client Login
                  </Link>
                )}
                <Link
                  to="/book-call"
                  onClick={() => setIsOpen(false)}
                  className="btn-primary text-center block"
                  data-testid="mobile-nav-book-call"
                >
                  Book a Strategy Call
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
